import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {EmptyDeps} from 'external/dev_april_corgi+/js/corgi/deps';
import {CorgiEvent, EventSpec} from 'external/dev_april_corgi+/js/corgi/events';
import {ACTION, CHANGED} from 'external/dev_april_corgi+/js/emu/events';

import {toZero} from './nodes/mixing_transformer';
import {
  ConcentrationValue,
  DataType,
  DataValue,
  TableType,
  TableValue,
  VolumeValue,
} from './types/data';
import {VolumeUnit, canonicalizeUnit, unitToNanos} from './types/units';

import {formatConcentration, formatVolume} from './node_element';
import {FLOAT_REGEX, deserializeFloat} from './parsing';

interface Args {
  onUpdate: (value: TableValue) => void;
  type: TableType;
}

export interface State {
  mode: 'csv' | 'table';
  csv: string | undefined;
  csvError: string | undefined;
  tableError: string | undefined;
  tableUpdate: boolean;
  value: TableValue;
}

export class TableEditorController extends Controller<
  Args,
  EmptyDeps,
  HTMLElement,
  State
> {
  private readonly onUpdate: (value: TableValue) => void;
  private readonly type: TableType;

  constructor(response: Response<TableEditorController>) {
    super(response);
    this.onUpdate = response.args.onUpdate;
    this.type = response.args.type;
    this.updateState({
      ...this.state,
      csv: this.toCsv(this.state.value),
    });
  }

  showCsv(): void {
    this.updateState({
      ...this.state,
      mode: 'csv',
    });
  }

  showTable(): void {
    this.updateState({
      ...this.state,
      mode: 'table',
    });
  }

  async csvChanged(e: CorgiEvent<EventSpec<unknown>>): Promise<void> {
    let now;
    try {
      now = this.fromCsv(
        (e.targetElement.element() as HTMLTextAreaElement).value,
      );
    } catch (e: unknown) {
      return this.updateState({
        ...this.state,
        csvError:
          e && typeof e === 'object' && 'message' in e
            ? String(e.message)
            : 'Invalid value',
      });
    }

    this.onUpdate(now);
    await this.updateState({
      ...this.state,
      // The user changed the CSV, so we shouldn't override it
      csv: undefined,
      csvError: undefined,
      value: now,
    });
  }

  async tableAddRow(): Promise<void> {
    const copied = copy(this.state.value);
    [...copied.values.values()].forEach((column, i) => {
      column.push(toZero(this.type.defaults[i]));
    });
    this.onUpdate(copied);
    await this.updateState({
      ...this.state,
      csv: this.toCsv(copied),
      csvError: undefined,
      tableUpdate: true,
      value: copied,
    });
  }

  async tableChanged(ev: CorgiEvent<typeof CHANGED>): Promise<void> {
    const col = checkExists(
      ev.actionElement.parent('[data-col]').data('col')?.number(),
    );
    const row = checkExists(
      ev.actionElement.parent('[data-row]').data('row')?.number(),
    );

    let asValue: DataValue;
    let tableError;
    const raw = ev.detail.value;
    try {
      asValue = deserialize(
        raw,
        this.type.columnTypes[col],
        this.type.defaults[col],
      );
      tableError = undefined;
    } catch (e: unknown) {
      asValue = {kind: 'string', value: raw};
      if (e && typeof e === 'object' && 'message' in e) {
        tableError = String(e.message);
      } else {
        tableError = 'Invalid value';
      }
    }

    const copied = copy(this.state.value);
    [...copied.values.values()][col][row] = asValue;
    this.onUpdate(copied);
    await this.updateState({
      ...this.state,
      csv: this.toCsv(copied),
      csvError: undefined,
      tableError,
      // The user changed the table, so don't update it
      tableUpdate: false,
      value: copied,
    });
  }

  async rowDelete(ev: CorgiEvent<typeof ACTION>): Promise<void> {
    const row = checkExists(
      ev.actionElement.parent('[data-row]').data('row')?.number(),
    );
    const copied = copy(this.state.value);
    for (const col of copied.values.values()) {
      col.splice(row, 1);
    }
    this.onUpdate(copied);
    await this.updateState({
      ...this.state,
      csv: this.toCsv(copied),
      tableUpdate: true,
      value: copied,
    });
  }

  private fromCsv(value: string): TableValue {
    const rows = value
      .split('\n')
      .map(r => r.trim())
      .filter(r => !!r);
    const header = splitRow(checkExists(rows.shift()));
    for (let i = 0; i < this.type.columnLabels.length; ++i) {
      if (header[i] !== this.type.columnLabels[i]) {
        throw new Error('Mismatch of expected header');
      }
    }

    const defaults = this.type.defaults;
    const types = this.type.columnTypes;
    const columns = this.type.columnLabels.map(_ => [] as DataValue[]);
    for (const row of rows) {
      const split = splitRow(row);
      for (let i = 0; i < columns.length; ++i) {
        const value = split[i];
        if (!value) {
          throw new Error('Missing value');
        }
        columns[i].push(deserialize(value, types[i], defaults[i]));
      }
    }
    return {
      kind: 'table',
      values: new Map(
        [...this.state.value.values.keys()].map((k, i) => [k, columns[i]]),
      ),
    };
  }

  private toCsv(value: TableValue): string {
    const header = [];
    for (const label of this.type.columnLabels) {
      if (label.includes(',')) {
        header.push('"' + label.replaceAll('"', '\\"') + '"');
      } else {
        header.push(label);
      }
    }
    const rows = [];
    rows.push(header.join(','));

    const columns = [...value.values.values()];
    const row = [];
    for (let i = 0; i < columns[0].length; ++i) {
      for (let j = 0; j < columns.length; ++j) {
        const value = columns[j][i];
        const serialized = serialize(value);
        if (serialized.includes(',')) {
          row.push('"' + serialized.replaceAll('"', '\\"') + '"');
        } else {
          row.push(serialized);
        }
      }
      rows.push(row.join(','));
      row.length = 0;
    }
    return rows.join('\n');
  }
}

function copy(value: TableValue): TableValue {
  return {
    kind: 'table',
    values: new Map(
      [...value.values.entries()].map(([key, column], i) => [key, [...column]]),
    ),
  };
}

export function deserialize(
  value: string,
  type: DataType,
  defaultValue: DataValue,
): DataValue {
  if (type.kind === 'boolean') {
    return {
      kind: 'boolean',
      value: ['1', 'true'].includes(value.toLowerCase()),
    };
  } else if (type.kind === 'concentration') {
    try {
      return deserializeConcentration(value.toLowerCase(), defaultValue);
    } catch (e: unknown) {
      // Fallback: try it as a volume. If it's not a volume, rethrow the original e
      try {
        return deserializeVolume(value.toLowerCase(), defaultValue);
      } catch (e2: unknown) {
        throw e;
      }
    }
  } else if (type.kind === 'string') {
    return {
      kind: 'string',
      value,
    };
  } else if (type.kind === 'volume') {
    return deserializeVolume(value.toLowerCase(), defaultValue);
  } else {
    throw new Error(`Unexpected kind ${type.kind}`);
  }
}

export function deserializeConcentration(
  value: string,
  defaultValue: DataValue,
): ConcentrationValue {
  if (value.trim() === '0' && defaultValue.kind === 'concentration') {
    return {
      ...defaultValue,
      number: 0,
    };
  }

  const {
    number: rawNumber,
    percent,
    unit: rawUnit,
  } = checkExists(
    value.match(
      new RegExp(
        /^\s*/.source + // ignore space at the start
          `${FLOAT_REGEX.source}` + // capture a number, possibly including a %
          /\s*/.source + // ignore spaces between number and unit
          /(?<unit>(?:(?:[a-z]g|u)\s*\/\s*)?[a-zμ]+)?/.source + // maybe capture a unit
          /\s*$/.source, // ignore space at the end
      ),
    )?.groups,
    'Expected a concentration',
  );
  let number;
  let unit;
  if (
    !rawUnit &&
    percent &&
    defaultValue.kind === 'concentration' &&
    defaultValue.unit === '%'
  ) {
    number = 100 * deserializeFloat(rawNumber);
    unit = defaultValue.unit;
  } else if (rawNumber.endsWith('%') && !rawUnit) {
    number = 100 * deserializeFloat(rawNumber);
    unit = '%';
  } else {
    number = deserializeFloat(rawNumber);
    unit = canonicalizeUnit(rawUnit);
    if (unit === 'L' || (unit.length === 2 && unit[1] === 'L')) {
      throw new Error('Unexpected volume');
    }
  }
  return {
    kind: 'concentration',
    number,
    unit,
  };
}

function deserializeVolume(
  value: string,
  defaultValue: DataValue,
): VolumeValue {
  if (value.trim() === '0') {
    return {kind: 'volume', numberNl: 0, preferredUnit: 'ul'};
  }

  const [_, rawNumber, rawUnit] = checkExists(
    value.match(/^\s*(\d*\.?\d+)\s*([A-Za-zμ]+)\s*$/),
    'Expected a volume',
  );
  const number = Number(rawNumber);
  if (!rawNumber || Number.isNaN(number)) {
    throw new Error(`Volume '${rawNumber}' must be a number`);
  }
  let volumeNumber;
  let unit;
  if (
    defaultValue.kind === 'concentration' &&
    defaultValue.unit
      .toLowerCase()
      .replaceAll(/\s*\//g, '/')
      .startsWith(rawUnit + '/')
  ) {
    volumeNumber = number / defaultValue.number;
    unit = defaultValue.unit
      .replaceAll(/\s*\/\s*/g, '/')
      .substring((rawUnit + '/').length)
      .toLowerCase();
  } else {
    volumeNumber = number;
    unit = canonicalizeUnit(rawUnit);
    if (unit !== 'L' && (unit.length !== 2 || unit[1] !== 'L')) {
      throw new Error('Expected a volume');
    }
  }
  return {
    kind: 'volume',
    numberNl: unitToNanos(volumeNumber, unit).number,
    preferredUnit: unit as VolumeUnit,
  };
}

export function serialize(value: DataValue): string {
  if (value.kind === 'boolean') {
    return String(value.value);
  } else if (value.kind === 'concentration') {
    return formatConcentration(value, /* round= */ false);
  } else if (value.kind === 'float') {
    let scale;
    let suffix;
    if (value.preferredDisplay === 'percentage') {
      scale = 100;
      suffix = '%';
    } else {
      scale = 1;
      suffix = '';
    }
    const roundingDigits = 1_000_000;
    return (
      String(
        Math.round(value.number * scale * roundingDigits) / roundingDigits,
      ) + suffix
    );
  } else if (value.kind === 'string') {
    return value.value;
  } else if (value.kind === 'volume') {
    return formatVolume(value.numberNl, /* round= */ false);
  } else {
    throw new Error(`Unexpected kind ${value.kind}`);
  }
}

function splitRow(text: string): string[] {
  const row = [];
  const building = [];
  const pending = [];
  let escaped = false;
  let quoted = false;
  let started = false;
  for (let i = 0; i < text.length; ++i) {
    const c = text[i];
    const isSpace = !c.trim();
    if (!started && !isSpace) {
      started = true;
    }

    if (!started) {
      continue;
    }

    if (escaped) {
      escaped = false;
      building.push(...pending, c);
      pending.length = 0;
    } else if (c === ',') {
      if (quoted) {
        building.push(c);
      } else {
        row.push(building.join(''));
        building.length = 0;
        pending.length = 0;
        started = false;
      }
    } else if (c === '\\') {
      escaped = true;
    } else if (c === '"') {
      if (quoted) {
        quoted = false;
      } else {
        building.push(...pending);
        pending.length = 0;
        quoted = true;
      }
    } else if (isSpace) {
      pending.push(c);
    } else {
      building.push(...pending, c);
      pending.length = 0;
    }
  }
  if (building.length > 0) {
    row.push(building.join(''));
  }
  return row;
}
